import { useState } from "react";
import { ListChecks } from "lucide-react";
import { toast } from "sonner";
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { updatePlanningEvent } from "../../services/api";
import { DayExceptionDialog } from "./DayExceptionDialog";
import { MobileWeekAgenda } from "./MobileWeekAgenda";
import { typeIcon, STATUS_META, PRIORITY_META } from "../../utils/eventVisuals";

export const ABS_COLORS = { cp: "#0ea5e9", maladie: "#f43f5e", rtt: "#8b5cf6", sans_solde: "#64748b", injustifiee: "#dc2626", formation: "#f59e0b", autre: "#94a3b8" };

const AXIS_START = 7 * 60;
const AXIS_END = 21 * 60;
const HOUR_W = 64;
const GRID_W = ((AXIS_END - AXIS_START) / 60) * HOUR_W;
const LANE_H = 26;
const EMP_COL_W = 190;
const HOURS = Array.from({ length: 15 }, (_, i) => 7 + i);

const timeLeft = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const mins = (h === 0 && m === 0 ? 24 * 60 : h * 60 + m);
  return Math.min(GRID_W, Math.max(0, ((mins - AXIS_START) / 60) * HOUR_W));
};

const addDaysIso = (dateIso, delta) => {
  const d = new Date(dateIso + "T00:00:00");
  d.setDate(d.getDate() + delta);
  return d.toLocaleDateString("fr-CA");
};
const toMin = (hhmm) => { const [h, m] = hhmm.split(":").map(Number); return h * 60 + m; };
const fromMin = (mins) => {
  const m = Math.max(0, Math.min(23 * 60 + 59, mins));
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};

const HBar = ({ start, end, color, top }) => {
  const left = timeLeft(start);
  const width = Math.min(GRID_W - left, Math.max(timeLeft(end) - left, 12));
  return (
    <div className="absolute rounded-md text-white overflow-hidden flex items-center justify-between px-1.5 pointer-events-none"
      style={{ top, height: LANE_H - 6, left: `${left / GRID_W * 100}%`, width: `${width / GRID_W * 100}%`, backgroundColor: color }}>
      <span className="text-[9px] font-semibold leading-none">{start}</span>
      {width > 76 && <span className="text-[9px] font-semibold leading-none opacity-85">{end}</span>}
    </div>
  );
};

const HFullBlock = ({ label, bg, top, striped }) => (
  <div className="absolute rounded-md flex items-center justify-center overflow-hidden pointer-events-none"
    style={{
      top, height: LANE_H - 6, left: 2, right: 2,
      backgroundColor: bg,
      backgroundImage: striped ? "repeating-linear-gradient(45deg, rgba(255,255,255,0.18) 0 6px, transparent 6px 12px)" : undefined,
    }}>
    <span className="text-[9px] font-bold text-white">{label}</span>
  </div>
);

const DraggableEventChip = ({ ev, date, assignees, onClick, canDrag }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: `${ev.id}:${date}`, data: { event: ev }, disabled: !canDrag });
  const TypeIcon = typeIcon(ev.event_type);
  const st = STATUS_META[ev.status] || STATUS_META.todo;
  const StatusIcon = st.icon;
  const prio = PRIORITY_META[ev.priority] || PRIORITY_META.normal;
  const cancelled = ev.status === "cancelled";
  return (
    <button
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`relative flex items-center gap-1 text-[9px] font-semibold text-white px-1.5 py-0.5 rounded-full hover:opacity-85 max-w-full transition-shadow ${isDragging ? "shadow-lg scale-[1.03] opacity-90" : ""} ${cancelled ? "opacity-50" : ""}`}
      style={{ backgroundColor: ev.color, transform: CSS.Translate.toString(transform), zIndex: isDragging ? 60 : undefined, touchAction: canDrag ? "none" : undefined }}
      onClick={onClick}
      data-testid={`event-chip-${ev.id}-${date}`}
      title={`${ev.title}${ev.start_time ? ` · ${ev.start_time}${ev.end_time ? "–" + ev.end_time : ""}` : ""}${ev.location ? ` · ${ev.location}` : ""} — ${st.label} · ${prio.label} — ${assignees.map((e) => e.first_name).join(", ") || "non attribué"}`}
    >
      <TypeIcon size={9} className="shrink-0" />
      <span className={`truncate ${cancelled ? "line-through" : ""}`}>{ev.title}</span>
      <StatusIcon size={9} className="shrink-0 opacity-90" />
      {ev.priority && ev.priority !== "normal" && (
        <span className="w-1.5 h-1.5 rounded-full shrink-0 ring-1 ring-white/70" style={{ backgroundColor: prio.color }}></span>
      )}
      {assignees.map((e) => (
        <span key={e.id} className="w-3.5 h-3.5 rounded-full bg-white/25 border border-white/60 flex items-center justify-center text-[7px] font-bold shrink-0">
          {(e.first_name?.[0] || "") + (e.last_name?.[0] || "")}
        </span>
      ))}
    </button>
  );
};

const DayHeaderDrop = ({ date, children, className }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `day|${date}` });
  return (
    <div ref={setNodeRef} className={`${className} ${isOver ? "ring-2 ring-inset ring-[#26788A] bg-teal-50" : ""}`} data-testid={`day-drop-${date}`}>
      {children}
    </div>
  );
};

const HourDrop = ({ date, hour }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `hour|${date}|${hour}` });
  return (
    <div
      ref={setNodeRef}
      className="absolute inset-y-0"
      style={{ left: `${(hour - 7) / 14 * 100}%`, width: `${100 / 14}%`, pointerEvents: "none", zIndex: 5 }}
    >
      {isOver && (
        <div className="absolute inset-0 bg-[#26788A]/20 border-x border-[#26788A] flex items-center justify-center">
          <span className="text-[9px] font-bold text-[#26788A] bg-white dark:bg-[#151517] px-1.5 rounded-full">{hour}h00</span>
        </div>
      )}
    </div>
  );
};

const EmpAvatar = ({ emp }) => (
  <span className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold shrink-0 overflow-hidden"
    style={{ backgroundColor: `${emp.color}22`, color: emp.color }}>
    {emp.photo ? <img src={emp.photo} alt="" className="w-full h-full object-cover" /> :
      `${(emp.first_name?.[0] || "").toUpperCase()}${(emp.last_name?.[0] || "").toUpperCase()}`}
  </span>
);

const _isPresent = (emp, dayMeta) => {
  if (emp.absences?.[dayMeta.date]) return false;
  const day = emp.days[dayMeta.day_name];
  if (!day || day.is_off || day.is_school || day.is_holiday_override) return false;
  return !!(day.morning_start || day.afternoon_start);
};

export const WeekGrid = ({ days, employees, allEmployees, events, tasks = [], focusDate, canManage, onEventClick, onEmployeeClick, onTaskClick, onChanged }) => {
  const [dragging, setDragging] = useState(false);
  const [excDialog, setExcDialog] = useState(null); // {emp, date}
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const initialsPool = allEmployees || employees;
  const empInitials = (ids) => initialsPool.filter((e) => (ids || []).includes(e.id));
  const eventsForDate = (dateIso) => events.filter((ev) => ev.date <= dateIso && dateIso <= (ev.end_date || ev.date));
  const tasksForDate = (dateIso) => tasks.filter((t) => t.due_date === dateIso);
  const TASK_CLS = {
    todo: "bg-violet-600 text-white border-violet-700",
    in_progress: "bg-amber-500 text-white border-amber-600",
    done: "bg-emerald-600 text-white border-emerald-700 line-through opacity-80",
  };

  const handleDragEnd = async (e) => {
    setDragging(false);
    const ev = e.active?.data?.current?.event;
    if (!ev || !e.over) return;
    const [kind, dateStr, hourStr] = String(e.over.id).split("|");
    const payload = {};
    if (dateStr && dateStr !== ev.date) {
      const delta = Math.round((new Date(dateStr) - new Date(ev.date)) / 86400000);
      payload.date = dateStr;
      if (ev.end_date) payload.end_date = addDaysIso(ev.end_date, delta);
    }
    if (kind === "hour") {
      const h = Number(hourStr);
      const newStart = `${String(h).padStart(2, "0")}:00`;
      if (newStart !== ev.start_time) {
        payload.start_time = newStart;
        if (ev.start_time && ev.end_time) {
          payload.end_time = fromMin(toMin(newStart) + (toMin(ev.end_time) - toMin(ev.start_time)));
        }
      }
    }
    if (!Object.keys(payload).length) return;
    try {
      await updatePlanningEvent(ev.id, payload);
      toast.success("Événement déplacé");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du déplacement");
    }
  };

  return (
    <DndContext sensors={sensors} onDragStart={() => setDragging(true)} onDragCancel={() => setDragging(false)} onDragEnd={handleDragEnd}>
    <MobileWeekAgenda days={days} employees={employees} allEmployees={allEmployees} events={events} tasks={tasks} focusDate={focusDate} canManage={canManage} onException={setExcDialog} onEmployeeClick={onEmployeeClick} onEventClick={onEventClick} onTaskClick={onTaskClick} />
    <div className="hidden lg:block bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="desktop-week-grid">
      {/* Légende employés */}
      <div className="flex flex-wrap gap-3 px-4 py-2.5 border-b-2 border-slate-300 bg-slate-50/60">
        {employees.map((emp) => (
          <button key={emp.id} className={`flex items-center gap-1.5 text-xs font-medium transition-opacity ${onEmployeeClick ? "hover:opacity-70" : "cursor-default"}`}
            style={{ color: emp.color }} onClick={() => onEmployeeClick?.(emp)} data-testid={`legend-emp-${emp.id}`}>
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: emp.color }}></span>
            {emp.first_name} {emp.last_name?.toUpperCase()} <span className="text-slate-400 font-normal">({(emp.weekly_hours || 0).toFixed(1)}h)</span>
          </button>
        ))}
        {canManage && (
          <span className="text-[10px] text-slate-400 ml-auto self-center">Glissez un événement pour le déplacer{onEmployeeClick ? " · Cliquez sur un employé pour ses horaires" : ""}</span>
        )}
      </div>

      <div>
        <div>
          {/* Ligne des heures */}
          <div className="flex border-b-2 border-slate-300 bg-slate-50/60">
            <div className="w-[150px] shrink-0 border-r-2 border-slate-300"></div>
            <div className="relative flex-1 min-w-0" style={{ height: 26 }}>
              {HOURS.map((h) => (
                <span key={h} className="absolute text-[10px] text-slate-400 font-mono" style={{ left: `${(h - 7) / 14 * 100}%`, transform: `translateX(${h === 7 ? 0 : h === 21 ? -100 : -50}%)`, top: 6 }}>{h}h</span>
              ))}
            </div>
            <div className="shrink-0 border-l-2 border-slate-300 flex items-center px-3" style={{ width: EMP_COL_W }}>
              <span className="text-[10px] font-bold text-slate-600">Employé(e)</span>
            </div>
          </div>

          {/* Lignes des jours */}
          {days.map((dayMeta) => {
            const dayEvents = eventsForDate(dayMeta.date);
            const n = Math.max(employees.length, 1);
            const rowH = Math.max(n * LANE_H + 8, 46);
            const presentCount = employees.filter((e) => _isPresent(e, dayMeta)).length;
            const isToday = dayMeta.date === new Date().toLocaleDateString("fr-CA");
            return (
              <div key={dayMeta.date} className={`flex border-b border-slate-200 last:border-b-0 ${dayMeta.is_sunday ? "bg-red-50/40" : ""} ${dayMeta.is_holiday ? "bg-amber-50/50" : ""} ${isToday ? "bg-[#26788A]/10 ring-2 ring-inset ring-[#26788A]" : ""}`} data-testid={isToday ? "today-row" : undefined}>
                {/* En-tête jour (zone de dépôt) */}
                <DayHeaderDrop date={dayMeta.date} className={`w-[150px] shrink-0 border-r-2 border-slate-300 p-1.5 overflow-hidden transition-colors ${isToday ? "bg-[#26788A]/15" : ""}`}>
                  <p className={`text-xs font-bold ${dayMeta.is_sunday ? "text-red-600" : "text-slate-800"}`}>
                    {dayMeta.label} <span className="font-normal text-slate-400">{new Date(dayMeta.date + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}</span>
                    {isToday && <span className="ml-1 text-[8px] font-bold uppercase bg-[#26788A] text-white px-1.5 py-0.5 rounded-full align-middle">Aujourd'hui</span>}
                  </p>
                  {dayMeta.is_holiday && <p className="text-[8px] text-amber-600 font-medium">{dayMeta.holiday_name}</p>}
                  <p className="text-[9px] font-semibold text-emerald-600 mt-0.5" data-testid={`present-count-${dayMeta.date}`}>
                    {presentCount} présent{presentCount > 1 ? "s" : ""}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-1" data-testid={`events-row-${dayMeta.date}`}>
                    {dayEvents.map((ev) => (
                      <DraggableEventChip key={ev.id} ev={ev} date={dayMeta.date} assignees={empInitials(ev.employee_ids)} canDrag={!!canManage} onClick={() => onEventClick?.(ev)} />
                    ))}
                    {tasksForDate(dayMeta.date).map((t) => (
                      <button
                        key={t.id}
                        onClick={(e) => { e.stopPropagation(); onTaskClick?.(t); }}
                        className={`flex items-center gap-1 max-w-full text-[9px] font-semibold px-1.5 py-0.5 rounded border ${TASK_CLS[t.status] || TASK_CLS.todo}`}
                        title={`Tâche : ${t.title}${t.assign_all ? " (tous)" : ""}`}
                        data-testid={`task-chip-${t.id}`}
                      >
                        <ListChecks size={9} className="shrink-0" />
                        <span className="truncate">{t.title}</span>
                        {!t.assign_all && empInitials(t.employee_ids).slice(0, 3).map((e2) => (
                          <span key={e2.id} className="text-[7px] font-bold opacity-70">{(e2.first_name || "")[0]}{(e2.last_name || "")[0]}</span>
                        ))}
                      </button>
                    ))}
                  </div>
                </DayHeaderDrop>

                {/* Grille horaire horizontale */}
                <div className="relative flex-1 min-w-0" style={{ minHeight: rowH }}>
                  {HOURS.map((h) => (
                    <div key={h} className="absolute inset-y-0 border-l border-slate-100" style={{ left: `${(h - 7) / 14 * 100}%` }}></div>
                  ))}
                  {employees.map((emp, i) => {
                    const day = emp.days[dayMeta.day_name];
                    const absence = emp.absences?.[dayMeta.date];
                    const top = 4 + i * LANE_H;
                    return (
                      <div key={emp.id} className="contents">
                        <div
                          className={`absolute left-0 right-0 ${canManage ? "cursor-pointer" : ""}`}
                          style={{ top, height: LANE_H }}
                          title={canManage ? `Cliquez pour poser une exception (École/Travail/Repos/Férié) — ${emp.first_name}` : undefined}
                          onClick={() => canManage && setExcDialog({ emp, date: dayMeta.date })}
                          data-testid={`lane-${emp.id}-${dayMeta.date}`}
                        ></div>
                        {absence ? (
                          <HFullBlock label={absence.label} bg={ABS_COLORS[absence.type] || "#94a3b8"} top={top} striped />
                        ) : day?.contract_status === "not_started" ? (
                          <HFullBlock label="Pas commencé" bg="#cbd5e1" top={top} striped />
                        ) : day?.contract_status === "ended" ? (
                          <HFullBlock label="Fin de contrat" bg="#475569" top={top} striped />
                        ) : day?.is_holiday_override ? (
                          <HFullBlock label="Férié" bg="#64748b" top={top} />
                        ) : day?.is_school ? (
                          <HFullBlock label="École" bg="#f59e0b" top={top} />
                        ) : day && !day.is_off ? (
                          <>
                            {day.morning_start && <HBar start={day.morning_start} end={day.morning_end} color={emp.color} top={top} />}
                            {day.afternoon_start && <HBar start={day.afternoon_start} end={day.afternoon_end} color={emp.color} top={top} />}
                          </>
                        ) : null}
                      </div>
                    );
                  })}
                  {dragging && HOURS.map((h) => (
                    <HourDrop key={`drop-${dayMeta.date}-${h}`} date={dayMeta.date} hour={h} />
                  ))}
                </div>

                {/* Colonne employés (nom + avatar alignés sur leur trait) */}
                <div className="relative shrink-0 border-l-2 border-slate-300" style={{ width: EMP_COL_W, minHeight: rowH }}>
                  {employees.map((emp, i) => (
                    <button key={emp.id}
                      className={`absolute left-0 right-0 flex items-center gap-1.5 px-2.5 ${onEmployeeClick ? "hover:bg-slate-50" : "cursor-default"}`}
                      style={{ top: 4 + i * LANE_H, height: LANE_H - 2 }}
                      onClick={() => onEmployeeClick?.(emp)}
                      data-testid={`row-emp-${emp.id}-${dayMeta.date}`}>
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: emp.color }}></span>
                      <EmpAvatar emp={emp} />
                      <span className="text-[10px] font-semibold truncate" style={{ color: emp.color }}>
                        {emp.first_name} {emp.last_name?.toUpperCase()}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
    <DayExceptionDialog
      open={!!excDialog}
      onClose={() => setExcDialog(null)}
      employee={excDialog?.emp}
      date={excDialog?.date}
      onChanged={onChanged}
    />
    </DndContext>
  );
};
